import React, { useState, useEffect, useRef } from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Loading from 'tbk-components/src/components/Loading';

// Extend the window object for hbspt support.
declare global {
	interface Window {
		hbspt?: any;
	}
}

export interface IHubSpotEmbedFormI18nProps {
	response?: string;
}

export interface IHubSpotGatedPageProps {
	link: string;
	cta: string;
	instructions?: string;
	businessUnit?: string;
}

export interface IHubSpotEmbedFormProps extends IBaseElementProps {
	formTitle?: string;
	/**
	 * @control richtext
	 */
	formDescription?: string;
	/**
	 * Copy-paste HubSpot embed form code here.
	 * @control richtext
	 */
	hubSpotEmbedCode?: string;
	/**
	 * @noUI
	 */
	downloadUrl?: string;
	/**
	 * @noUI
	 */
	gatedPage?: IHubSpotGatedPageProps;
	/**
	 * @noUI
	 */
	i18n: IHubSpotEmbedFormI18nProps;
}

let hubSpotCount = 0;
let hubSpotLoaded = false;

/**
 * HubSpot Embed Form Component
 * @icon list-view
 * @block
 */
const HubSpotEmbedForm: React.FC<IHubSpotEmbedFormProps> = ({
	id,
	className,
	classNames = [],
	hubSpotEmbedCode,
	formTitle,
	formDescription,
	downloadUrl,
	gatedPage,
	i18n,
}) => {
	const [isLoading, setIsLoading] = useState(true);
	const [hubSpotFormSubmitted, setHubSpotFormSubmitted] = useState(false);
	const [isIframeEmbed, setIsIframeEmbed] = useState(false);
	const ref = useRef(null);
	const formID = useRef(hubSpotCount);
	const fullIdName = `hubspotForm-${formID.current}`;
	const cookieName = gatedPage?.businessUnit ? `gated-${gatedPage.businessUnit}` : 'gated-resources';
	const cookieExpireSeconds = 120; // Seconds until cookie expires TODO ask the client how long this should be

	useEffect(() => {
		if (!hubSpotEmbedCode) return;

		if (hubSpotEmbedCode.includes('<iframe')) {
			setIsIframeEmbed(true);
			setIsLoading(false);
			return;
		}

		let formDataObj = {};
		// Use regular expression to extract key-value pairs.
		const regex = /(\w+): "([^"]+)"/g;
		const matches = hubSpotEmbedCode.match(regex);

		if (matches) {
			for (const match of matches) {
				const [key, value] = match
					.split(':')
					.map((item) => item.trim());

				formDataObj[key] = value.replace(/"/g, '');
			}

			const targetEl = ref.current.querySelector('.hs-form__target');
			if (targetEl) {
				targetEl.id = fullIdName;
			}

			formDataObj['target'] = '#' + fullIdName;
		} else {
			formDataObj = null;
		}

		const hsScript = document.createElement('script');

		hsScript.src = 'https://js.hsforms.net/forms/embed/v2.js';
		hsScript.id = 'hubspot-script';

		if (!document.getElementById('hubspot-script')) {
			document.body.appendChild(hsScript);
		}

		const createHSForm = async () => {
			// Check if the hubspot object is available
			// Although the script is loaded, the object may not be available yet
			// @ts-ignore
			while (!window.hbspt) {
				await new Promise((resolve) => setTimeout(resolve, 1000)); // Wait for 1 second.
			}
			// @ts-ignore
			if (window.hbspt) {
				// @ts-ignore
				window.hbspt.forms.create(formDataObj);
			}
		};

		const assignClassOnFocus = () => {
			if (!ref.current) return;

			const inputs = ref.current.querySelectorAll('.hs-input') as NodeListOf<HTMLInputElement>;
			if (inputs.length < 1) return;

			inputs.forEach((input) => {
				// Assign the current href of the window to the hidden field.
				if ('current_url' === input.name) {
					input.value = window.location.href;
				}

				if (input.value) {
					input
						.closest('.hs-form-field')
						?.classList.add('has-value');
				}

				if (input instanceof HTMLSelectElement && input.selectedIndex !== -1) {
					input
						.closest('.hs-form-field')
						?.classList.add('has-value');
				}

				input.addEventListener('focus', () => {
					input
						.closest('.hs-form-field')
						?.classList.add('is-active');
				});

				input.addEventListener('blur', () => {
					if (!input.value) {
						input
							.closest('.hs-form-field')
							?.classList.remove('is-active');
					}
				});

			});
		};

		const fixAodaIssues = () => {
			if (!ref.current) return;

			const fieldsets = ref.current.querySelectorAll(
				'.hs-form__target form fieldset',
			) as NodeListOf<HTMLFieldSetElement>;

			fieldsets.forEach((fieldset) => {

				const maybeLegend = fieldset.querySelector(
					':scope > legend',
				) as HTMLLegendElement;

				if (!maybeLegend) {
					const legend = document.createElement('legend');
					const lang = document.documentElement.lang;

					legend.classList.add('visually-hidden');
					legend.innerHTML =
						lang === 'fr-CA' || lang === 'fr-FR'
							? 'Ligne de formulaire'
							: 'Form row';

					fieldset.prepend(legend);
				}
			});
		};

		const preFillInput = (target) => {
			if (!ref.current) return;
			// Name attribute should be exact match of the input label
			// However, I found cases HubSpot adds other strings in the name attribute
			// So we use the wildcard (*) to match the name attribute
			// If there's more than one target selected, we won't perform the pre-fill function
			const targetField = ref.current.querySelectorAll(
				`input[name*="${target}"]`,
			) as NodeListOf<HTMLInputElement>;

			if (targetField.length === 1 && downloadUrl) {
				targetField[0].value = downloadUrl;
			}
		};

		// We're disabling this function for now because it will prevent form submission.
		const resolveDuplicateIds = () => {
			// HubSpot injects recaptcha script for each form
			// every script has a unique src hash but a duplicate id
			// so we only need to change the id of the recaptcha script
			const recaptcha = document.querySelectorAll('#recaptcha');
			const gResponse = document.querySelectorAll('#g-recaptcha-response');
			const hsResponse = document.querySelectorAll('#hs-recaptcha-response');

			if (!recaptcha || recaptcha.length < 2) return;

			recaptcha.forEach((item, index) => (item.id = `recaptcha-${index}`));
			gResponse.forEach((item, index) => {
				item.id = `g-recaptcha-response-${index}`;
				item.setAttribute('aria-label', `Google reCAPTCHA ${i18n?.response || 'response'} ${index}`);
			});

			hsResponse.forEach((item, index) => {
				item.id = `hs-recaptcha-response-${index}`;
				item.setAttribute('aria-label', `HubSpot reCAPTCHA ${i18n?.response || 'response'} ${index}`);
			});
		};

		const maybeGatedPageRedirect = () => {
			setHubSpotFormSubmitted(true);
			if (gatedPage?.link) {
				window.location.href = gatedPage.link;
			}
		};

		const getDisplayHeight = (target) => {
			if (!target) return;

			const form = target.querySelector('form');
			target.style.maxHeight = form ? `${form.offsetHeight}px` : '100%';
			target.style.opacity = '1';
		};

		const handleLoad = (event) => {
			if (event.data.type === 'hsFormCallback') {
				switch (event.data.eventName) {
					case 'onFormReady':
						assignClassOnFocus();
						setIsLoading(false);
						fixAodaIssues();
						// we need to pre-fill the download_url input field
						// coordinates the marketing team to create a hidden field with the name "download_url"
						preFillInput('download_url');
						(document.cookie.indexOf(cookieName) !== -1) ? maybeGatedPageRedirect() : null ;// cookie exists, redirect to gated page.
						break;
					case 'onFormSubmitted':
						// Set a cookie to remember the user has submitted the form.
						const submittedMessage = document.querySelector(`.hs-form-${event.data.id}`);
						submittedMessage?.scrollIntoView({ behavior: 'smooth', block: 'center' });
						document.cookie = `${cookieName}=true; max-age=${cookieExpireSeconds}; path=/`;
						maybeGatedPageRedirect();
						break;
				}

				// Update the form container height to display the form content
				setTimeout(() => {
					getDisplayHeight(
						ref.current.querySelector('.hs-form__target'),
					);
				}, 600);
			}
		};

		if (hubSpotLoaded) {
			createHSForm();
		} else {
			hsScript.addEventListener('load', createHSForm);
			hubSpotLoaded = true;
		}

		window.addEventListener('message', handleLoad);
		window.addEventListener('resize', () =>
			getDisplayHeight(ref.current.querySelector('.hs-form__target')),
		);

		hubSpotCount++;

		return () => {
			hsScript.removeEventListener('load', createHSForm);
			window.removeEventListener('message', handleLoad);
			window.removeEventListener('resize', () =>
				getDisplayHeight(ref.current.querySelector('.hs-form__target')),
			);
			document.getElementById(fullIdName)?.remove();

			// Remove the added hs script only if there's one hs form on dismount.
			if (document.getElementById('hubspot-script') && hubSpotCount === 1) {
				document.body.removeChild(hsScript);
			}
		};
	}, []);

	return (
		<div
			className={className || cn('hs-form__container', ...classNames)}
			id={id}
			ref={ref}
		>
			<div className="hs-form__container-inner">
				{isLoading && <Loading />}
				{(formTitle || formDescription) && (
					<div className="hs-form__header-content px-1">
						{formTitle && <h2 className="hs-form__title h3 mb-2 mb-md-3">{formTitle}</h2>}
						{formDescription && (
							<p
								className="hs-form__description mb-2 mb-md-3"
								dangerouslySetInnerHTML={{ __html: formDescription }}
							/>
						)}
					</div>
				)}
				{hubSpotEmbedCode && (
					<div className="hs-form__target" style={{ maxHeight: '45rem', opacity: 1 }}>
						{isIframeEmbed && (
							<div dangerouslySetInnerHTML={{ __html: hubSpotEmbedCode }} />
						)}
					</div>
				)}
			</div>
			{gatedPage && hubSpotFormSubmitted && (
				<div className="hs-form__gated d-flex flex-column justify-content-center mt-4">
					{gatedPage?.instructions && (
						<p className="text-white text-center">{gatedPage.instructions}</p>
					)}
					{gatedPage?.cta && gatedPage?.link && (
						<a className="btn btn-secondary" href={gatedPage.link}>
							{gatedPage.cta}
						</a>
					)}
				</div>
			)}
		</div>
	);
};

export default HubSpotEmbedForm;
